// src/HomePage/Game/FolkGame/OAnQuan/OAnQuanIndex.js
import React, { useState, useEffect } from 'react';
import {
  subscribeFolkRooms,
  subscribeRoomDetail,
  getRoomSnapshot,
  createFolkRoom,
  joinFolkRoom,
  quickJoinFolkRoom,
  leaveFolkRoom,
  togglePlayerReady,
  kickPlayerFromRoom
} from '../FolkGameService';
import OAnQuanLobby from './OAnQuanLobby';
import OAnQuanWaitingRoom from './OAnQuanWaitingRoom';
import OAnQuanGame from './OAnQuanGame';
import { createInitialOAnQuanBoard } from './OAnQuanLogic';

const OAnQuanIndex = ({ currentUser, onBackToPortal }) => {
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeRoom, setActiveRoom] = useState(null);
  const [isBotMode, setIsBotMode] = useState(false);

  // 1. Khôi phục phiên khi F5 tải lại trang
  useEffect(() => {
    const restoreSession = async () => {
      const savedRoomId = sessionStorage.getItem('active_oanquan_room_id');
      if (savedRoomId && currentUser?.id) {
        setIsLoading(true);
        const roomData = await getRoomSnapshot('oanquan', savedRoomId);
        if (
          roomData &&
          (roomData.player1?.id === currentUser.id || roomData.player2?.id === currentUser.id)
        ) {
          setActiveRoom(roomData);
        } else {
          sessionStorage.removeItem('active_oanquan_room_id');
        }
        setIsLoading(false);
      }
    };
    restoreSession();
  }, [currentUser?.id]);

  // 2. Lắng nghe danh sách phòng ở Sảnh Ô Ăn Quan
  useEffect(() => {
    if (activeRoom) return;
    setIsLoading(true);
    const unsubscribe = subscribeFolkRooms('oanquan', (roomList) => {
      setRooms(roomList);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [activeRoom]);

  // 3. Lắng nghe chi tiết phòng & Kiểm tra bị Kick
  useEffect(() => {
    if (!activeRoom?.id || isBotMode) return;

    const unsubscribe = subscribeRoomDetail('oanquan', activeRoom.id, (updatedRoom) => {
      if (!updatedRoom) {
        sessionStorage.removeItem('active_oanquan_room_id');
        setActiveRoom(null);
        return;
      }

      if (
        updatedRoom.kickedPlayerId === currentUser?.id &&
        updatedRoom.player2?.id !== currentUser?.id
      ) {
        sessionStorage.removeItem('active_oanquan_room_id');
        setActiveRoom(null);
        setErrorMessage('Bạn đã bị chủ phòng mời ra khỏi bàn chơi!');
        return;
      }

      setActiveRoom(updatedRoom);
      sessionStorage.setItem('active_oanquan_room_id', updatedRoom.id);
    });

    return () => unsubscribe();
  }, [activeRoom?.id, isBotMode, currentUser?.id]);

  const handleCreateRoom = async (name, password) => {
    if (!currentUser?.id) {
      setErrorMessage('Vui lòng đăng nhập để tạo bàn!');
      return;
    }
    setIsLoading(true);
    const result = await createFolkRoom('oanquan', {
      name,
      password,
      player1: currentUser
    });
    setIsLoading(false);

    if (result.success) {
      setIsBotMode(false);
      setActiveRoom(result.room);
      sessionStorage.setItem('active_oanquan_room_id', result.roomId);
    } else {
      setErrorMessage(result.message || 'Không thể tạo bàn chơi.');
    }
  };

  const handleJoinRoom = async (roomId, password) => {
    if (!currentUser?.id) {
      setErrorMessage('Vui lòng đăng nhập để tham gia!');
      return;
    }
    setIsLoading(true);
    const res = await joinFolkRoom('oanquan', roomId, currentUser, password);
    setIsLoading(false);

    if (res.success) {
      setIsBotMode(false);
      setActiveRoom(res.room);
      sessionStorage.setItem('active_oanquan_room_id', roomId);
    } else {
      setErrorMessage(res.message);
    }
  };

  const handleQuickMatch = async () => {
    if (!currentUser?.id) {
      setErrorMessage('Vui lòng đăng nhập để ghép bàn tự động!');
      return;
    }
    setIsLoading(true);
    const res = await quickJoinFolkRoom('oanquan', currentUser);
    setIsLoading(false);

    if (res.success) {
      setIsBotMode(false);
      setActiveRoom(res.room);
      sessionStorage.setItem('active_oanquan_room_id', res.room.id);
    } else {
      setErrorMessage(res.message);
    }
  };

  const handlePlayWithBot = (difficulty = 'hard') => {
    const diffTitles = {
      easy: { name: 'AI Robot (Dễ) 🤖', icon: '🌱' },
      medium: { name: 'AI Robot (Trung Bình) 🤖', icon: '⚔️' },
      hard: { name: 'AI Robot (Cao Thủ) 🤖', icon: '👑' }
    };
    const selectedDiff = diffTitles[difficulty] || diffTitles.hard;

    setIsBotMode(true);
    sessionStorage.removeItem('active_oanquan_room_id');
    setActiveRoom({
      id: 'bot_room_oanquan',
      name: `Luyện tập - ${selectedDiff.name}`,
      botDifficulty: difficulty,
      player1: currentUser || { id: 'guest', fullName: 'Bạn', holyName: '', ready: true },
      player2: {
        id: 'bot',
        fullName: selectedDiff.name,
        holyName: selectedDiff.icon,
        isCatechist: false,
        ready: true
      },
      status: 'playing',
      startingPlayerId: currentUser?.id || 'guest',
      currentTurn: currentUser?.id || 'guest',
      score: { p1: 0, p2: 0 },
      gameState: { board: createInitialOAnQuanBoard(), p1Score: 0, p2Score: 0 }
    });
  };

  const handleLeaveActiveRoom = async () => {
    if (activeRoom && !isBotMode && currentUser?.id) {
      await leaveFolkRoom('oanquan', activeRoom.id, currentUser.id);
    }
    sessionStorage.removeItem('active_oanquan_room_id');
    setActiveRoom(null);
    setIsBotMode(false);
  };

  const handleKickPlayer = async () => {
    if (activeRoom && !isBotMode && currentUser?.id) {
      await kickPlayerFromRoom('oanquan', activeRoom.id, currentUser.id);
    }
  };

  const handleToggleReady = async () => {
    if (!activeRoom || isBotMode || !currentUser?.id) return;
    const isPlayer1 = activeRoom.player1?.id === currentUser.id;
    const currentReady = isPlayer1 ? activeRoom.player1?.ready : activeRoom.player2?.ready;
    await togglePlayerReady('oanquan', activeRoom.id, isPlayer1, !currentReady);
  };

  // 1. ĐANG THI ĐẤU
  if (activeRoom && (activeRoom.status === 'playing' || activeRoom.status === 'finished')) {
    return (
      <OAnQuanGame
        room={activeRoom}
        currentUser={currentUser}
        isBotMode={isBotMode}
        onLeave={handleLeaveActiveRoom}
      />
    );
  }

  // 2. PHÒNG CHỜ SẴN SÀNG
  if (activeRoom && activeRoom.status === 'waiting') {
    return (
      <OAnQuanWaitingRoom
        room={activeRoom}
        currentUser={currentUser}
        onToggleReady={handleToggleReady}
        onKickPlayer={handleKickPlayer}
        onLeave={handleLeaveActiveRoom}
      />
    );
  }

  // 3. SẢNH CHỜ
  return (
    <OAnQuanLobby
      rooms={rooms}
      isLoading={isLoading}
      currentUser={currentUser}
      onCreateRoom={handleCreateRoom}
      onJoinRoom={handleJoinRoom}
      onQuickMatch={handleQuickMatch}
      onPlayWithBot={handlePlayWithBot}
      onBackToPortal={onBackToPortal}
      errorMessage={errorMessage}
      onClearError={() => setErrorMessage('')}
    />
  );
};

export default OAnQuanIndex;